import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { ToastrService } from 'ngx-toastr';
import { LoginRegisterService } from '../login-register/login-register.service';
import { CandidateService } from './candidate.service';
import * as crypto from "crypto-js";
import { toasterMessages } from '@messages/*';
import { DeviceDetectorService } from 'ngx-device-detector';
@Component({
  selector: 'app-candidate',
  templateUrl: './candidate.component.html',
  styleUrls: ['./candidate.component.scss']
})

export class CandidateComponent implements OnInit {

  showPassword: boolean = false;
  isForgotPasswordClicked: boolean;
  token: any;
  assessmentId: any;
  isExistingUser = false;
  companyName: string;
  isMobile: boolean = false;
  private fromChangePassword = false;
  passwordConditions: string = 'Password must be at least 6 characters long.';
  hasEmail: boolean = true;  // Default to email
  hasPhone: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private candidateService: CandidateService,
    private route: ActivatedRoute,
    private loginRegisterService: LoginRegisterService,
    private storage: StorageMap,
    private deviceService: DeviceDetectorService) {
    this.isMobile = this.deviceService.isMobile();
    this.isForgotPasswordClicked = false;
    this.route.queryParams.subscribe(params => {
      this.token = params.token;
      this.fromChangePassword = params.changePwd;
    });
  }

  candidateLoginForm = this.fb.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],  // ALWAYS REQUIRED
    phone: [''],  // Optional
    password: ['', [Validators.required, Validators.minLength(6)]]
  });

  ngOnInit() {
    this.isCandidateExist();
    if (this.fromChangePassword) {
      this.isExistingUser = true;
      this.candidateLoginForm.get('name').clearValidators();
      this.candidateLoginForm.get('name').updateValueAndValidity();
    }
  }

  requestResetLink() {
    // Extract email from form
    const email = this.candidateLoginForm.get('email').value;

    if (!email || !email.trim()) {
      this.toastr.error('Please enter your email address');
      return;
    }

    this.loginRegisterService.forgotPassword({ email }).subscribe(res => {
      this.toastr.success(toasterMessages.candidateResetLink);
      this.isForgotPasswordClicked = false; // Return to login screen
    }, (err) => {
      this.toastr.error(err?.error?.errors[0]?.error, 'Error');
    });
  }

  isCandidateExist() {
    if (this.token) {
      this.candidateService.isCandidateExist(this.token).subscribe((response) => {
        this.isExistingUser = response.candidateExists;
        this.assessmentId = response.assessmentId;
        this.companyName = response.companyName;

        // Check if candidate has email or phone from response
        this.hasEmail = !!(response.email && response.email.trim());
        this.hasPhone = !!(response.phone && response.phone.trim());

        // **Email is always required**
        this.candidateLoginForm.controls.email.setValidators([Validators.required, Validators.email]);

        if (this.hasEmail) {
          // Pre-fill email from invitation
          this.candidateLoginForm.controls.email.setValue(response.email);
        }

        if (this.hasPhone) {
          // Pre-fill phone if available (optional)
          this.candidateLoginForm.controls.phone.setValue(response.phone);
        }

        this.candidateLoginForm.controls.email.updateValueAndValidity();
        this.candidateLoginForm.controls.phone.updateValueAndValidity();

        this.candidateLoginForm.controls.name.setValue(response.candidateName);
        if (this.isExistingUser) {
          this.candidateLoginForm.get('name').clearValidators();
          this.candidateLoginForm.get('name').updateValueAndValidity();
        }
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error');
      });
    }
  }

  forgotPassword() {
    this.isForgotPasswordClicked = true;
  }

  backToLogin(): void {
    this.isForgotPasswordClicked = false;
  }

  terms() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['terms-and-conditions'])
    );
    window.open(url, '_blank');
  }

  /** view assessment for 2 calls one is login one is register */
  signInOrRegister() {
    this.candidateLoginForm.value.name = this.candidateLoginForm.value.name?.trim();
    const reqObj = { ...{ token: this.token }, ...this.candidateLoginForm.value, ...{ closePreviousSessions: true } };
    reqObj.password = crypto.AES.encrypt(reqObj.password?.trim(), "evaltrixSecret").toString();
    this.candidateService.candidateLoginRequest(reqObj).subscribe((response) => {
      localStorage.setItem('token', response.token);
      this.candidateLoginForm.controls.email.setValue('');
      this.candidateLoginForm.controls.password.setValue('');
      if (response?.assessments?.length === 1) {
        // Redirecting to Welcome page.
        const assessmentId = response.assessments[0]?.assessmentId;
        if (response?.assessments[0]?.status === 'NEW') {
          this.storage.delete('assessments').subscribe(() => { });
          this.storage.set('welcomeViewData', response).subscribe(() => {
            this.router.navigate([`candidate/candidate-welcome/${assessmentId}`]);
          });
        } else if (response?.assessments[0]?.status === 'COMPLETED') {
          this.toastr.info(toasterMessages.candidateNoActiveAssessmentFound);
        } else {
          // Redirect to assessment modules 
          this.storage.delete('assessments').subscribe(() => { });
          this.storage.set('welcomeViewData', response).subscribe(() => {
            this.router.navigate(['candidate/assessment-categories'],
              { queryParams: { id: assessmentId } });
          });
        }
      } else if (response.assessments?.length > 1) {
        let isAssessmentCompleted = response.assessments.every(obj => obj["status"] === "COMPLETED");
        if (isAssessmentCompleted) {
          this.toastr.info(toasterMessages.candidateNoActiveAssessmentFound);
          return;
        }
        const assessments = {
          candidateName: response.candidateName,
          email: response.email,
          assessments: response.assessments
        };
        this.storage.delete('welcomeViewData').subscribe(() => { });
        this.storage.set('assessments', assessments).subscribe(() => {
          this.router.navigate(['candidate/pendingAssessments']);
        });
      } else {
        this.toastr.success(toasterMessages.candidateNoAssessmentFound);
      }
    }, (err) => {
      this.candidateLoginForm.controls.password.setValue('');
      this.toastr.error(err?.error?.errors[0]?.error, 'Error');
    });
  }
}
